import React from 'react'
import DataTable, {TableAction, TableColumn} from '../../../../shared/components/DataTable'
import {formatDateTime} from '../shared/DocumentApi'
import {ITagModel} from '../tag/TagModel'
import {TagChips} from '../tag/TagChips'
import {SelectCheckbox} from '../shared/Selection'
import {
  fullName,
  IApplicationModel,
  TRACKING_STATUS_BADGE,
  trackingStatusLabel,
} from './ApplicationModel'

type Props = {
  data: IApplicationModel[]
  currentPage: number
  totalPages: number
  pageSize?: number
  pageSizeOptions?: number[]
  onPageSizeChange?: (size: number) => void
  onPageChange: (page: number) => void
  /** Verilmezse satır işlemleri gösterilmez (dashboard) */
  onDetail?: (application: IApplicationModel) => void
  onEdit?: (application: IApplicationModel) => void
  onStartProcess?: (application: IApplicationModel) => void
  onTag?: (application: IApplicationModel) => void
  onRemoveTag?: (application: IApplicationModel, tag: ITagModel) => void
  /** Verilirse ilk sütunda seçim kutusu gösterilir */
  selectedIds?: string[]
  onToggleSelect?: (id: string) => void
  onAssignCall?: (application: IApplicationModel) => void
  onDelete?: (application: IApplicationModel) => void
  loading?: boolean
}

export const ApplicationTable: React.FC<Props> = ({
  data,
  currentPage,
  totalPages,
  pageSize,
  pageSizeOptions,
  onPageSizeChange,
  onPageChange,
  onDetail,
  onEdit,
  onStartProcess,
  onTag,
  onRemoveTag,
  selectedIds,
  onToggleSelect,
  onAssignCall,
  onDelete,
  loading = false,
}) => {
  const columns: TableColumn<IApplicationModel>[] = [
    {
      key: 'firstname',
      label: 'Aday',
      render: (_, application) => (
        <div className='d-flex flex-column'>
          <span className='fw-bolder text-gray-800'>{fullName(application)}</span>
          <span className='text-muted fs-7'>{application.email}</span>
          {application.phone && <span className='text-muted fs-7'>{application.phone}</span>}
          <TagChips
            tags={application.tags}
            onRemove={onRemoveTag ? (tag) => onRemoveTag(application, tag) : undefined}
          />
        </div>
      ),
    },
    {
      key: 'jobTitle',
      label: 'İlan / Süreç',
      render: (_, application) => (
        <div className='d-flex flex-column'>
          <span className='fw-semibold'>{application.jobTitle}</span>
          <span className='text-muted fs-7'>{application.hrProcessName || 'Süreç bağlanmamış'}</span>
        </div>
      ),
    },
    {
      key: 'createdDate',
      label: 'Başvuru Tarihi',
      width: '150px',
      render: (value) => <span className='fs-7'>{formatDateTime(value)}</span>,
    },
    {
      key: 'trackingStatus',
      label: 'Süreç Durumu',
      width: '170px',
      render: (_, application) =>
        application.trackingStatus ? (
          <div className='d-flex flex-column gap-1'>
            <span className={`badge ${TRACKING_STATUS_BADGE[application.trackingStatus]} align-self-start`}>
              {trackingStatusLabel(application.trackingStatus)}
            </span>
            <span className='text-muted fs-7'>{application.currentStepName}</span>
          </div>
        ) : application.outcome === 'NEGATIVE' ? (
          <span className='badge badge-light-danger'>Olumsuz</span>
        ) : (
          <span className='badge badge-light'>Başlatılmadı</span>
        ),
    },
  ]

  if (selectedIds && onToggleSelect) {
    columns.unshift({
      key: 'select',
      label: '',
      width: '40px',
      render: (_, application) =>
        application.id ? (
          <SelectCheckbox
            checked={selectedIds.includes(application.id)}
            onChange={() => onToggleSelect(application.id as string)}
          />
        ) : null,
    })
  }

  const actions: TableAction<IApplicationModel>[] = []
  if (onAssignCall) {
    actions.push({
      label: 'Arama Havuzuna Aktar',
      icon: 'bi bi-telephone-plus',
      className: 'btn-light-success me-2',
      onClick: onAssignCall,
    })
  }
  if (onStartProcess) {
    actions.push({
      label: 'Süreci Başlat',
      icon: 'bi bi-play-fill',
      className: 'btn-light-success me-2',
      onClick: onStartProcess,
      disabled: (application) => !!application.trackingId,
    })
  }
  if (onTag) {
    actions.push({
      label: 'Etiketle',
      icon: 'bi bi-tags',
      className: 'btn-light-warning me-2',
      onClick: onTag,
      // Etiket havuzdaki adaya takılır; eski başvurular önce Aday Havuzu'na aktarılmalı
      disabled: (application) => !application.candidateId,
    })
  }
  if (onDetail) {
    actions.push({label: 'Detay', icon: 'bi bi-eye', className: 'btn-light-info me-2', onClick: onDetail})
  }
  if (onEdit) {
    actions.push({label: 'Düzenle', icon: 'bi bi-pencil', className: 'btn-light-primary me-2', onClick: onEdit})
  }
  if (onDelete) {
    actions.push({label: 'Sil', icon: 'bi bi-trash', className: 'btn-light-danger', onClick: onDelete})
  }

  return (
    <DataTable
      data={data}
      columns={columns}
      actions={actions}
      showPagination={true}
      currentPage={currentPage}
      totalPages={totalPages}
      pageSize={pageSize}
      pageSizeOptions={pageSizeOptions}
      onPageSizeChange={onPageSizeChange}
      onPageChange={onPageChange}
      loading={loading}
      emptyMessage='Başvuru bulunamadı'
    />
  )
}
